import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  ClipboardDocumentListIcon, ComputerDesktopIcon, TableCellsIcon, Cog8ToothIcon,
  HomeIcon, BuildingStorefrontIcon, EllipsisHorizontalIcon,
  ChartPieIcon, BanknotesIcon, CurrencyDollarIcon, CalculatorIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import '../styles/comprehensive-responsive-system.css';

const VendorBottomNavigation: React.FC = () => {
  const { user } = useAuth();
  const { theme, currentThemeName } = useTheme();
  const location = useLocation();
  const { pathname, search } = location;
  
  // Only show Home button when using modernDashboard theme
  const isModernTheme = currentThemeName === 'modernDashboard';
  const isScandinavianWarm = currentThemeName === 'scandinavianWarm';

  // Theme-aware bottom nav colors with fallbacks
  const navBackground = theme.components?.bottomNav?.background || theme.background;
  const navBorder = theme.components?.bottomNav?.border || theme.components?.card?.border;
  const activeColor = theme.components?.bottomNav?.activeIndicator || theme.primary;
  const inactiveColor = theme.components?.bottomNav?.inactiveIcon || theme.gray?.[500] || '#8A8A8A';

  // Adding a class to the body to add padding at the bottom
  useEffect(() => {
    document.body.classList.add('has-bottom-nav');
    return () => {
      document.body.classList.remove('has-bottom-nav');
    };
  }, []);

  const isFinanceOnlySteward = !!user?.financeOnlySteward;
  if (!user || (!user.roles.includes('ROLE_VENDOR') && !isFinanceOnlySteward)) {
    return null;
  }

  const ordersPath = "/vendor/orders";
  const tablesPath = "/vendor/tables";
  const menuPath = `/admin/vendors/${user?.id}/menu`;
  const kitchenDisplayPath = "/vendor/kitchen";
  const morePath = "/vendor/settings";

  const isOrdersActive = pathname === ordersPath || (pathname === ordersPath && (search === "?status=pending" || search === ""));
  const isTablesActive = pathname === tablesPath || pathname.startsWith("/vendor/tables");
  const isMenuActive = pathname.includes("/menu");
  const isKitchenDisplayActive = pathname === kitchenDisplayPath;
  const isMoreActive = pathname === morePath || (pathname.startsWith("/vendor/") && !pathname.includes("orders") && !pathname.includes("kitchen") && !pathname.includes("tables") && !pathname.includes("menu") && !pathname.includes("dashboard") && !pathname.startsWith("/vendor/finance"));

  // Context-aware: finance section uses dedicated nav
  const isFinanceContext = pathname.startsWith('/vendor/finance');

  type Tab = { to: string; label: string; Icon: React.ComponentType<{ className?: string }>; active: boolean };

  const financeTabs: Tab[] = [
    { to: '/vendor/finance', label: 'Overview', Icon: ChartPieIcon, active: pathname === '/vendor/finance' || pathname === '/vendor/finance/dashboard' },
    { to: '/vendor/finance/expenses', label: 'Expenses', Icon: BanknotesIcon, active: pathname.startsWith('/vendor/finance/expenses') },
    { to: '/vendor/finance/income', label: 'Income', Icon: CurrencyDollarIcon, active: pathname.startsWith('/vendor/finance/income') },
    { to: '/vendor/finance/eod-summary', label: 'EOD', Icon: CheckCircleIcon, active: pathname.startsWith('/vendor/finance/eod') },
    { to: ordersPath, label: 'Home', Icon: HomeIcon, active: false },
  ];

  // Restricted steward gets only the three add-only tabs; no Overview, no Home.
  const financeOnlyTabs: Tab[] = [
    { to: '/vendor/finance/expenses', label: 'Expenses', Icon: BanknotesIcon, active: pathname.startsWith('/vendor/finance/expenses') },
    { to: '/vendor/finance/income', label: 'Income', Icon: CurrencyDollarIcon, active: pathname.startsWith('/vendor/finance/income') },
    { to: '/vendor/finance/eod-summary', label: 'EOD', Icon: CheckCircleIcon, active: pathname.startsWith('/vendor/finance/eod') },
  ];

  const defaultTabs: Tab[] = [
    { to: ordersPath, label: 'Orders', Icon: ClipboardDocumentListIcon, active: isOrdersActive },
    { to: tablesPath, label: 'Floor', Icon: TableCellsIcon, active: isTablesActive },
    { to: menuPath, label: 'Menu', Icon: BuildingStorefrontIcon, active: isMenuActive },
    { to: kitchenDisplayPath, label: 'Kitchen', Icon: ComputerDesktopIcon, active: isKitchenDisplayActive },
    { to: '/vendor/finance', label: 'Finance', Icon: ChartPieIcon, active: false },
  ];

  const tabs = isFinanceOnlySteward
    ? financeOnlyTabs
    : isFinanceContext
      ? financeTabs
      : defaultTabs;

  return (
    <>
      <div
        className="fixed bottom-0 left-0 w-full z-[1002] flex justify-around items-center lg:hidden vendor-bottom-nav"
        style={{
          backgroundColor: navBackground,
          borderTop: navBorder ? `1px solid ${navBorder}` : undefined,
          fontFamily: 'var(--font-family-base)',
          height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {tabs.map(({ to, label, Icon, active }) => (
          <Link
            key={label}
            to={to}
            className="flex flex-col items-center justify-center transition-all duration-200 rounded-lg relative group py-responsive-sm px-responsive-sm touch-target"
            style={{
              fontSize: '0.72rem',
              color: active ? activeColor : inactiveColor,
              fontWeight: active ? '600' : '400',
              backgroundColor: 'transparent',
            }}
          >
            {isScandinavianWarm && active && (
              <div style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: activeColor, marginBottom: 2 }} />
            )}
            <Icon className={`w-6 h-6 ${isScandinavianWarm ? '' : 'mb-0.5'} transition-transform duration-200 ${active && !isScandinavianWarm ? 'scale-110' : ''}`} />
            <span className="tracking-wide">{label}</span>
            {active && !isScandinavianWarm && (
              <div className="absolute left-2 right-2 h-[2px] rounded-full" style={{ bottom: '-3px', backgroundColor: activeColor }} />
            )}
          </Link>
        ))}
      </div>
    </>
  );
};

export default VendorBottomNavigation;
